import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Truck, Check } from 'lucide-react';
import { useAmendMode, AmendBanner } from './useAmendMode';
import './NativeInventoryAdjustmentNew.css';

// Fulfillment (Native) — ship goods without NetSuite in the loop.
// Pick the order two ways: a native (manual) sales order from the list, or a
// NetSuite SO number typed in (open lines come from the sales_orders mirror,
// never live NS). Edit ship quantities per line, pick the ship-from location,
// post. Posting: FIFO COGS per line + ledger quantity relief + DR COGS / CR
// inventory-asset JE + order-side record + best-effort NS itemFulfillment
// transform (echo-deduped, externalId NATIVE-FUL-<id>).
// Reuses the adjustment screen's stylesheet (same house template).

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};
const newRef = () => `FUL-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function NativeFulfillmentNew() {
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [writesEnabled, setWritesEnabled] = useState(null);

  // order source: 'native' (manual native SO) | 'netsuite' (NS SO number)
  const [mode, setMode] = useState('native');
  const [nativeOrders, setNativeOrders] = useState([]);
  const [order, setOrder] = useState(null);       // { kind, id?, nsSoId?, number, customer }
  const [soQuery, setSoQuery] = useState('');
  const [loadingOrder, setLoadingOrder] = useState(false);

  // lines: [{ key, itemIdOrSku, sku, name, open, shipQty }]
  const [lines, setLines] = useState([]);

  const [shipMethod, setShipMethod] = useState('');
  const [tracking, setTracking] = useState('');
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const sourceKey = useRef(newRef());
  const amend = useAmendMode();
  const [amendOfNumber, setAmendOfNumber] = useState(null);

  // Deep-link prefill (?so=<order number>): jump straight to NetSuite-SO mode
  // with the order's open lines loaded — Count's sales-order Fulfill button.
  useEffect(() => {
    const so = new URLSearchParams(window.location.search).get('so');
    if (!so) return;
    setMode('netsuite');
    setSoQuery(so);
    lookupNsSo(so);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link prefill (?nativeOrderId=<uuid>): native-only orders (no NS
  // twin — lockout captures, store-PO generations) have no ?so= mirror row
  // to look up, so the record page's Fulfill button links here instead.
  useEffect(() => {
    const nativeOrderId = new URLSearchParams(window.location.search).get('nativeOrderId');
    if (!nativeOrderId) return;
    setMode('native');
    pickNativeOrder({ id: nativeOrderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.get('/api/native-inventory-adjustments/locations')
      .then(({ data }) => setLocations((data.locations || []).filter((l) => l.is_active !== false)))
      .catch(() => setLocations([]));
    axios.get('/api/native-fulfillments')
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
    axios.get('/api/native-orders', { params: { limit: 50 } })
      .then(({ data }) => setNativeOrders(data.orders || []))
      .catch(() => setNativeOrders([]));
  }, []);

  // Amend (?amendOf=<fulfillment id>): prefill the order, location and shipped
  // lines from the shipment being corrected. The order is reconstructed from
  // the document's own columns rather than re-looked-up, because the original
  // shipment is the authority on what it shipped against.
  useEffect(() => {
    if (!amend.amendOf || locations.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-fulfillments/${encodeURIComponent(amend.amendOf)}`);
        if (cancelled || !data.fulfillment) return;
        const f = data.fulfillment;
        const loc = locations.find((l) => l.id === f.location_id);
        if (loc) setLocation(loc);
        setMode(f.native_order_id ? 'native' : 'netsuite');
        setOrder({
          kind: f.native_order_id ? 'native' : 'netsuite',
          id: f.native_order_id || undefined,
          nsSoId: f.ns_so_id || undefined,
          number: f.ns_so_id ? `NS SO ${f.ns_so_id}` : 'native order',
          customer: '',
        });
        setShipMethod(f.ship_method || '');
        setTracking(f.tracking || '');
        setMemo(f.memo || '');
        setAmendOfNumber(f.fulfillment_number);
        setLines((data.lines || []).map((l, i) => ({
          key: `amend-${i}`,
          itemIdOrSku: l.item_id || l.sku,
          sku: l.sku,
          name: l.sku,
          open: Number(l.quantity),
          shipQty: String(Number(l.quantity)),
        })));
      } catch { /* prefill is best-effort; the form stays blank */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const pickNativeOrder = async (o) => {
    setLoadingOrder(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/native-orders/${encodeURIComponent(o.id)}`);
      const ord = data.order;
      const open = (ord.lines || [])
        .map((l) => ({
          key: l.id,
          itemIdOrSku: l.sku,
          sku: l.sku,
          name: l.description || l.name || l.sku,
          open: Math.max(0, Number(l.quantity) - Number(l.quantity_fulfilled || 0)),
        }))
        .filter((l) => l.open > 0)
        .map((l) => ({ ...l, shipQty: String(l.open) }));
      if (open.length === 0) {
        setError(`${ord.order_number || 'Order'} has no open lines to ship.`);
        return;
      }
      setOrder({ kind: 'native', id: ord.id, nsSoId: ord.netsuite_so_id || null, number: ord.order_number || ord.id, customer: ord.customer_name || ord.customer_email || '' });
      setLines(open);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingOrder(false);
    }
  };

  // soValue is a string when called from the ?so= deep link; the button's
  // onClick passes the click event, which falls through to the typed query.
  const lookupNsSo = async (soValue) => {
    const q = (typeof soValue === 'string' ? soValue : soQuery).trim();
    if (!q) return;
    setLoadingOrder(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/native-fulfillments/so-lines', { params: { so: q } });
      const open = (data.lines || [])
        .filter((l) => l.open > 0 && (l.itemId || l.sku))
        .map((l) => ({
          key: `${l.lineNumber}`,
          itemIdOrSku: l.itemId || l.sku,
          sku: l.sku || l.nsItemId,
          name: l.name,
          open: l.open,
          shipQty: String(l.open),
        }));
      if (open.length === 0) {
        setError(`${data.salesOrder.orderNumber} has no open, item-mapped lines to ship.`);
        return;
      }
      setOrder({ kind: 'netsuite', nsSoId: data.salesOrder.nsSoId, number: data.salesOrder.orderNumber, customer: data.salesOrder.customerName || '' });
      setLines(open);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingOrder(false);
    }
  };

  const setShipQty = (key, v) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, shipQty: v } : l)));
  const removeLine = (key) => setLines((ls) => ls.filter((l) => l.key !== key));
  const clearOrder = () => { setOrder(null); setLines([]); setError(null); };

  const shippable = lines.filter((l) => Number(l.shipQty) > 0);
  const canPost = order && location && shippable.length > 0 && !submitting
    && (!amend.isAmend || amend.amendReasonValid);

  const post = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(amend.postUrl('/api/native-fulfillments'), {
        sourceKey: sourceKey.current,
        orderRef: {
          nativeOrderId: order.kind === 'native' ? order.id : undefined,
          nsSoId: order.nsSoId || undefined,
        },
        locationId: location.id,
        lines: shippable.map((l) => ({ itemIdOrSku: l.itemIdOrSku, quantity: Number(l.shipQty) })),
        shipMethod: shipMethod.trim() || undefined,
        tracking: tracking.trim() || undefined,
        memo: memo.trim() || undefined,
        ...amend.bodyExtras(),
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      sourceKey.current = newRef(); // a failed post gets a fresh idempotency key
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const f = confirmation.fulfillment;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{f.fulfillment_number} posted</h2>
          <div className="nia-confirm-grid">
            <span>Order</span><strong>{order?.number}</strong>
            <span>Shipped from</span><strong>{location?.name}</strong>
            <span>Lines</span><strong>{f.line_count}</strong>
            <span>COGS</span><strong>{money(f.total_cogs)}</strong>
            <span>Quantity leg</span><strong>{f.movement_status}{f.movement_error ? ` (${f.movement_error})` : ''}</strong>
            <span>Costing</span><strong>{f.costing_status}{f.costing_error ? ` (${f.costing_error})` : ''}</strong>
            <span>GL</span><strong>{f.gl_status}{f.gl_error ? ` (${f.gl_error})` : ''}</strong>
            <span>Order record</span><strong>{f.order_record_status}{f.order_record_error ? ` (${f.order_record_error})` : ''}</strong>
            <span>NetSuite</span><strong>{f.ns_status}{f.ns_transaction_id ? ` (${f.ns_transaction_id})` : ''}</strong>
          </div>
          <p className="nia-note">{confirmation.netsuiteNote}</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>New fulfillment</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Truck size={20} /> {amend.isAmend ? 'Amend Fulfillment' : 'Fulfillment (Native)'}</h1>
        {writesEnabled === false && (
          <div className="nia-banner">Posting is disabled (NATIVE_FULFILLMENTS_ENABLED is off on the Run service). Browsing works; posting will 403.</div>
        )}
      </div>

      <AmendBanner amend={amend} docLabel={amendOfNumber} what="the shipment" />

      <div className="nia-card">
        <h3>1. Order</h3>
        {!order ? (
          <>
            <div className="nia-locations" style={{ marginBottom: 12 }}>
              <button className={`nia-location ${mode === 'native' ? 'selected' : ''}`} onClick={() => setMode('native')}>Native order</button>
              <button className={`nia-location ${mode === 'netsuite' ? 'selected' : ''}`} onClick={() => setMode('netsuite')}>NetSuite SO number</button>
            </div>
            {mode === 'native' ? (
              nativeOrders.length === 0 ? (
                <div className="nia-hint">No native (manual) orders found. Use the NetSuite SO number tab for synced orders.</div>
              ) : (
                <div className="nia-results">
                  {nativeOrders.map((o) => (
                    <button key={o.id} className="nia-result" onClick={() => pickNativeOrder(o)}>
                      <code>{o.order_number || o.id.slice(0, 8)}</code> {o.customer_name || o.customer_email || ''}
                      <span className="nia-result-muted">{o.status}</span>
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="nia-input nia-input-search"
                  placeholder="SO number (e.g. SO151207) or NS internal id…"
                  value={soQuery}
                  onChange={(e) => setSoQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') lookupNsSo(); }}
                />
                <button className="nia-btn" onClick={lookupNsSo} disabled={loadingOrder || !soQuery.trim()}>
                  {loadingOrder ? 'Loading…' : 'Load open lines'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            <code>{order.number}</code> {order.customer}
            {order.nsSoId && <span className="nia-result-muted"> NS SO {order.nsSoId}</span>}
            <button className="nia-icon-btn" onClick={clearOrder}>×</button>
          </div>
        )}
        {loadingOrder && !order && <div className="nia-hint" style={{ marginTop: 8 }}>Loading order lines…</div>}
      </div>

      <div className="nia-card">
        <h3>2. Ship-from location</h3>
        <div className="nia-locations">
          {locations.map((l) => (
            <button
              key={l.id}
              className={`nia-location ${location?.id === l.id ? 'selected' : ''}`}
              onClick={() => setLocation(l)}
            >
              {l.name}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="nia-error">{error}</div>}

      {order && lines.length > 0 && (
        <div className="nia-card">
          <h3>3. Lines — {order.number}</h3>
          <table className="nia-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="r">Open</th>
                <th className="r">Ship now</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key}>
                  <td><code>{l.sku}</code> {l.name}</td>
                  <td className="r">{qty(l.open)}</td>
                  <td className="r">
                    <input
                      className="nia-input nia-input-num"
                      inputMode="decimal"
                      value={l.shipQty}
                      onChange={(e) => setShipQty(l.key, e.target.value)}
                    />
                  </td>
                  <td className="r">
                    <button className="nia-icon-btn" onClick={() => removeLine(l.key)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Ship method (optional)
              <input className="nia-input" value={shipMethod} onChange={(e) => setShipMethod(e.target.value)} maxLength={200} />
            </label>
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Tracking (optional)
              <input className="nia-input" value={tracking} onChange={(e) => setTracking(e.target.value)} maxLength={200} />
            </label>
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Memo (optional)
              <input className="nia-input" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
            </label>
          </div>
          <div className="nia-footer">
            <div className="nia-footer-totals">
              <span>Shipping <strong>{shippable.length}</strong> line{shippable.length === 1 ? '' : 's'}</span>
            </div>
            <div className="nia-actions">
              <button className="nia-btn nia-btn-primary" onClick={post} disabled={!canPost || writesEnabled === false}>
                {submitting ? 'Posting…' : amend.isAmend ? 'Reverse and repost' : 'Post fulfillment'}
              </button>
            </div>
          </div>
          <p className="nia-hint" style={{ marginTop: 8 }}>
            Posting consumes FIFO cost layers per line (COGS), relieves native on-hand at the chosen location,
            posts a DR COGS / CR inventory JE, advances the native order, and creates the NetSuite item
            fulfillment when an NS sales order exists and NS is reachable (echo-deduped either way).
          </p>
        </div>
      )}
    </div>
  );
}
